import React, { useEffect, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, FieldError, useForm } from 'react-hook-form'
import {
  CreateIsotopeDto,
  IsotopeDto,
  IsotopeEmissionType,
  IsotopeUsage,
} from '@services/dtos'
import { isotopeSchema } from '@utils/schemas'
import { useAppDispatch } from '@hooks/reduxHooks'
import { Checkbox, FormControlLabel, FormGroup, FormHelperText, Grid, Typography } from '@mui/material'
import {
  createIsotope,
  updateIsotope,
} from '@state/reducers/isotopeReducer'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import InputField from '../inputs/InputField'
import { FormCrudHeader } from './FormCrudHeader'
import { IsotopeEmissionTypeLabels, IsotopeEmissionTypeShortLabels, IsotopeUsageLabels } from '@utils/constants'
import SelectField from '../inputs/SelectField'
import { humanizeDurationSeconds } from '@utils/date.utils'

const EMISSION_TYPE_OPTIONS = (
  Object.entries(IsotopeEmissionTypeLabels) as Array<[IsotopeEmissionType, string]>
).map(([value, label]) => ({ label, value }))

type Props = {
  editingIsotope?: IsotopeDto | null
  onClose: () => void
}

const IsotopeForm: React.FC<Props> = ({
  editingIsotope,
  onClose,
}) => {
  const {
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors },
  } = useForm<CreateIsotopeDto>({
    defaultValues: editingIsotope
      ? {
          code: editingIsotope.code,
          name: editingIsotope.name,
          atomicNumber: editingIsotope.atomicNumber,
          physicalHalfLifeS: editingIsotope.physicalHalfLifeS,
          emissions: editingIsotope.emissions ?? [],
          massNumber: editingIsotope.massNumber,
          peakEnergyKev: editingIsotope.peakEnergyKev,
          meanEnergyKev: editingIsotope.meanEnergyKev,
          notes: editingIsotope.notes ?? undefined,
          usages: editingIsotope.usages ?? [],
        }
      : {
          usages: [],
          emissions: []
        },
    resolver: yupResolver(isotopeSchema),
  })

  const dispatch = useAppDispatch()

  const [isSubmitting, setIsSubmitting] = useState(false)

  const cb = async (msg: string) => {
    dispatch(
      enqueueSnackbar({
        message: msg,
        options: { variant: 'success' },
      }),
    )
  }

  useEffect(() => {
    return () => {
      reset()
    }
  }, [dispatch])

  const onSubmitForm = async (data: CreateIsotopeDto) => {
    setIsSubmitting(true)

    if (editingIsotope) {
      dispatch(updateIsotope({ id: editingIsotope.id, dto: data }))
        .unwrap()
        .then(() => {
          cb('L\'isotope a été modifié avec succès')
          onClose()
        })
        .catch(() => cb('Une erreur est survenue, vérifiez que le symbole n\'est pas déjà utilisé'))
        .finally(() => setIsSubmitting(false))
    } else {
      dispatch(createIsotope(data))
        .unwrap()
        .then(() => {
          cb('L\'isotope a été créé avec succès')
          onClose()
        })
        .catch(() => cb('Une erreur est survenue, vérifiez que le symbole n\'est pas déjà utilisé'))
        .finally(() => setIsSubmitting(false))
    }
  }

  const physicalHalfLifeS = watch('physicalHalfLifeS')
  const formattedPhysicalHalfLifeS = humanizeDurationSeconds(physicalHalfLifeS)

  const renderFields = (fields: any[]) => {
    return fields.map((field) => (
      <Grid item xs={12} sm={field.sm ?? 6} key={field.name}>
        <InputField
          control={control}
          name={field.name}
          label={field.label}
          type={field.type ?? 'text'}
          multiline={field.type === 'textarea'}
          rows={field.type === 'textarea' ? 2 : undefined}
          preventNegative={field.type === 'number'}
        />
        {field.name === 'physicalHalfLifeS' && formattedPhysicalHalfLifeS && (
          <Typography variant="caption" color="text.secondary">
            = {formattedPhysicalHalfLifeS}
          </Typography>
        )}
      </Grid>
    ))
  }

  return (
    <div className="flex w-full h-full p-4">
      <form 
        onSubmit={(e) => {
          e.stopPropagation()
          handleSubmit(onSubmitForm)(e)
        }}
        className="w-full"
      >
        <FormCrudHeader
          isSubmitting={isSubmitting}
          onClose={onClose}
          editing={!!editingIsotope}
        />

        <div className="max-w-screen-sm mx-auto mt-4">
          <fieldset className="border border-gray-200 p-4 rounded-lg">
            <legend className="text-lg font-bold mb-4 border border-gray-200 p-2 rounded-lg">
              Isotope
            </legend>

            <Grid container rowSpacing={2} columnSpacing={4} mt={1}>
              {renderFields([
                { name: 'code', label: 'Code' },
                { name: 'name', label: 'Label'},
                { name: 'massNumber', label: 'Nombre de masse', type: 'number' },
                { name: 'physicalHalfLifeS', label: 'Demi-vie (s)', type: 'number' },
                { name: 'atomicNumber', label: 'Numéro atomique', type: 'number' }
              ])}

              <Grid item xs={12} sm={6}>
                <SelectField
                  control={control}
                  name="emissions"
                  options={EMISSION_TYPE_OPTIONS}
                  value={watch('emissions')}
                  label="Émissions"
                  multiple
                  renderValue={(selected) =>
                    (selected as IsotopeEmissionType[])
                      .map((v) => IsotopeEmissionTypeShortLabels[v])
                      .join(', ')
                  }
                />
              </Grid>

              {renderFields([
                { name: 'peakEnergyKev', label: 'Pic énergie principal (keV)', type: 'number' },
                { name: 'meanEnergyKev', label: 'Énergie moyenne beta (keV)', type: 'number' },
              ])}

              <Controller
                name="usages"
                control={control}
                defaultValue={[]}
                render={({ field }) => {
                  const value = field.value ?? []
                  const handleToggle = (usage: IsotopeUsage) => (
                    event: React.ChangeEvent<HTMLInputElement>
                  ) => {
                    if (event.target.checked) {
                      field.onChange([...value, usage])
                    } else {
                      field.onChange(value.filter((item: IsotopeUsage) => item !== usage))
                    }
                  }
                  return (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Usages
                      </Typography>
                      <FormGroup row sx={{ flexWrap: 'nowrap', gap: 2 }}>
                        <FormControlLabel
                          sx={{ mr: 0 }}
                          control={
                            <Checkbox
                              checked={value.includes(IsotopeUsage.DIAGNOSTIC)}
                              onChange={handleToggle(IsotopeUsage.DIAGNOSTIC)}
                            />
                          }
                          label={IsotopeUsageLabels[IsotopeUsage.DIAGNOSTIC]}
                          slotProps={{ typography: { variant: 'body2' } }}
                        />
                        <FormControlLabel
                          sx={{ mr: 0 }}
                          control={
                            <Checkbox
                              checked={value.includes(IsotopeUsage.TREATMENT)}
                              onChange={handleToggle(IsotopeUsage.TREATMENT)}
                            />
                          }
                          label={IsotopeUsageLabels[IsotopeUsage.TREATMENT]}
                          slotProps={{ typography: { variant: 'body2' } }}
                        />
                      </FormGroup>
                      {errors.usages && (
                        <FormHelperText error>
                          {(errors.usages as unknown as FieldError).message}
                        </FormHelperText>
                      )}
                    </Grid>
                  )
                }}
              />

              {renderFields([{ name: 'notes', label: 'Notes', type: 'textarea', sm: 12 }])}
            </Grid>
          </fieldset>
        </div>
      </form>
    </div>
  )
}

export default IsotopeForm
